import { Component, OnInit } from '@angular/core';
import { BadeLoftService } from '../bade-loft.service';
import { ResponseStatus } from 'src/app/shared/enums';
import { CommonService } from 'src/app/services/common.service';

@Component({
  selector: 'app-shopify-connection',
  templateUrl: './shopify-connection.component.html',
  styleUrls: ['./shopify-connection.component.css'],
})
export class ShopifyConnectionStatusComponent implements OnInit {
  authDetails: any;
  isFetchingAuthDetails: boolean = true;
  isTestingConnection: boolean = false;
  isDisconnecting: boolean = false;
  isConnectionError: boolean = false;
  shopifyAuthUrl: string = '';
  isRegisteringWebhooks: boolean = false;
  isDeregisteringWebhooks: boolean = false;
  registeredWebhooks: string[] = [];
  // Table-friendly view of registeredWebhooks: each "topic → address" string split into columns.
  webhookRows: { topic: string; address: string }[] = [];
  isLoadingWebhooks: boolean = false;

  constructor(private _blS: BadeLoftService, public _cS: CommonService) {}

  ngOnInit(): void {
    // Fetch non-secret config from the backend (avoids embedding config in environment.ts) and
    // build the OAuth authorization URL with a sessionStorage state nonce (CSRF defense-in-depth).
    this._blS.GetShopifyConnectionConfig().subscribe((response: any) => {
      if (
        response &&
        response.responseStatus == ResponseStatus.Success &&
        response.result
      ) {
        const cfg = response.result;
        const nonce =
          Math.random().toString(36).substring(2) + Date.now().toString(36);
        sessionStorage.setItem('shopify_oauth_state', nonce);
        this.shopifyAuthUrl =
          'https://' +
          cfg.storeDomain +
          '/admin/oauth/authorize?client_id=' +
          encodeURIComponent(cfg.clientId || '') +
          '&scope=' +
          encodeURIComponent(cfg.scopes || '') +
          '&redirect_uri=' +
          encodeURIComponent(cfg.redirectUri || '') +
          '&state=' +
          encodeURIComponent(nonce);
      }
    });

    this.ShopifyAuthConnectionTest();
  }

  // `test` is truthy only when triggered by the "Test Connection" button (HTML passes 'test').
  // ngOnInit calls this with no argument, so the on-load check stays silent — toasts fire only
  // for the explicit user action.
  ShopifyAuthConnectionTest(test?: any) {
    this.isTestingConnection = true;
    this._blS.ShopifyAuthConnectionTest().subscribe(
      (response: any) => {
        this.isTestingConnection = false;
        if (response && response.responseStatus == ResponseStatus.Success) {
          this.isConnectionError = false;
          this.isFetchingAuthDetails = true;
          this.getAuthDetails();
          if (test) {
            this._cS.displayMessage('success', 'Shopify connection is active.');
          }
        } else {
          const msg = ((response && response.message) || '').toLowerCase();
          this.isConnectionError =
            msg.indexOf('invalid') >= 0 || msg.indexOf('revoked') >= 0;
          this.authDetails = null;
          this.isFetchingAuthDetails = false;
          if (test) {
            this._cS.displayMessage(
              'error',
              (response && response.message) || 'Shopify connection failed.'
            );
          }
        }
      },
      () => {
        this.isTestingConnection = false;
        this.isFetchingAuthDetails = false;
        if (test) {
          this._cS.displayMessage('error', 'Shopify connection test failed.');
        }
      }
    );
  }

  getAuthDetails() {
    try {
      this._blS.GetShopifyAuthDetail().subscribe((response: any) => {
        if (response && response.responseStatus == ResponseStatus.Success) {
          this.authDetails = response.result;
          if (this.authDetails && this.authDetails.connectionStatus) {
            this.loadRegisteredWebhooks();
          }
        }
        this.isFetchingAuthDetails = false;
      });
    } catch (error) {
      this.isFetchingAuthDetails = false;
    }
  }

  disconnectFromShopify() {
    this.isDisconnecting = true;
    this._blS.revokeShopifyAuth().subscribe(
      (response: any) => {
        this.isDisconnecting = false;
        this.authDetails = null;
        this.isConnectionError = false;
        this._cS.displayMessage('success', 'Disconnected from Shopify.');
      },
      () => {
        this.isDisconnecting = false;
        this._cS.displayMessage('error', 'Failed to disconnect from Shopify.');
      }
    );
  }

  registerWebhooks() {
    this.isRegisteringWebhooks = true;
    this._blS.registerShopifyWebhooks().subscribe(
      () => {
        this.isRegisteringWebhooks = false;
        this._cS.displayMessage('success', 'Webhooks registered.');
        this.loadRegisteredWebhooks();
      },
      () => {
        this.isRegisteringWebhooks = false;
        this._cS.displayMessage('error', 'Failed to register webhooks.');
      }
    );
  }

  deregisterWebhooks() {
    this.isDeregisteringWebhooks = true;
    this._blS.deregisterShopifyWebhooks().subscribe(
      () => {
        this.isDeregisteringWebhooks = false;
        this._cS.displayMessage('success', 'Webhooks deregistered.');
        this.loadRegisteredWebhooks();
      },
      () => {
        this.isDeregisteringWebhooks = false;
        this._cS.displayMessage('error', 'Failed to deregister webhooks.');
      }
    );
  }

  loadRegisteredWebhooks() {
    this.isLoadingWebhooks = true;
    this._blS.getShopifyWebhooks().subscribe(
      (response: any) => {
        this.isLoadingWebhooks = false;
        // These endpoints return the payload directly (no ResponseModel wrapper), so read
        // response.webhooks; fall back to response.result.webhooks if a wrapper is ever added.
        this.registeredWebhooks =
          (response && response.webhooks) ||
          (response && response.result && response.result.webhooks) ||
          [];
        // Split each "topic → address" string into table columns. A string without the
        // " → " separator is treated as a topic with no address.
        this.webhookRows = this.registeredWebhooks.map((w) => {
          const idx = w.indexOf(' → ');
          return idx === -1
            ? { topic: w, address: '' }
            : { topic: w.slice(0, idx), address: w.slice(idx + 3) };
        });
      },
      () => {
        this.isLoadingWebhooks = false;
      }
    );
  }
}
